import Seo from "@/components/Seo";
import { Link } from "react-router-dom";
import { Shield, Lock, Eye, EyeOff, Ban, Accessibility } from "lucide-react";

export default function Trust() {
    const pillars = [
        {
            icon: EyeOff,
            title: "Your calculator inputs never leave your browser",
            body: "Every calculation runs client-side, in JavaScript on your own device. Your mortgage balance, salary, retirement savings, and debt totals are never transmitted to a server, never logged, never stored. The only data we collect is anonymous page-view analytics (and only if you consent). If our servers were breached tomorrow, there would be nothing of yours inside.",
        },
        {
            icon: Ban,
            title: "We don't sell, rent, or share your data",
            body: "FinCalc does not operate an email newsletter that rents your address to third parties. We don't broker leads to lenders. We don't have a sneaky 'partners' list in our Privacy Policy. Our revenue model is display advertising — Google AdSense shows ads, we get paid per impression, and that's the entire arrangement. No affiliate kickbacks, no steering, no selling you out.",
        },
        {
            icon: Shield,
            title: "Ad-fraud protection, built in",
            body: "We run server-side click-pattern detection to catch abusive IP patterns that would otherwise get an AdSense account banned. We have an emergency kill-switch that can instantly disable all advertising sitewide if Google's systems flag unusual traffic. This matters to you because an ad-fraud incident would mean ads stop loading — and we've engineered around it.",
        },
        {
            icon: Lock,
            title: "Modern security, not security theatre",
            body: "HTTPS everywhere. Strict Content Security Policy. HSTS. X-Frame-Options. Rate limiting on every write endpoint. A honeypot-protected contact form. A published security.txt for responsible disclosure. None of this is exotic — it's just the minimum a well-run 2026 website should do.",
        },
        {
            icon: Accessibility,
            title: "Accessible by design (WCAG 2.1 AAA)",
            body: "Keyboard navigation on every interactive element. Screen-reader announcements for calculator results. Dyslexia-friendly font toggle. High-contrast theme. Larger-text toggle. Reduced-motion support. A full Accessibility Statement. Financial decisions affect everyone — our calculators should too.",
        },
        {
            icon: Eye,
            title: "Open about how we work",
            body: "The math behind every calculator is the same math a professional would use — standard amortization formulas, compound interest, IRS brackets, real safe-withdrawal rules. No secret algorithms, no 'proprietary scoring.' Every assumption is printed next to the result. If a number looks wrong to you, we want to hear about it — we publish corrections.",
        },
    ];

    return (
        <>
            <Seo
                title="Privacy-First, Ad-Fraud Protected Finance Calculators | FinCalc"
                description="FinCalc is the privacy-first, ad-fraud-protected finance calculator hub. Your inputs never leave your browser. No tracking. No data selling. WCAG AAA accessible."
                canonical="/trust"
            />
            <section className="container-editorial py-16 max-w-5xl">
                <div className="text-[11px] uppercase tracking-[0.2em] text-accent">
                    Our commitments
                </div>
                <h1 className="font-serif text-5xl lg:text-7xl text-primary mt-3 leading-[1.05] tracking-tight text-balance">
                    Built on trust,<br />
                    <span className="italic">not tracking.</span>
                </h1>
                <p className="mt-5 max-w-2xl text-lg text-foreground/80 leading-relaxed">
                    Most finance sites are lead-generation funnels wearing a calculator costume.
                    FinCalc isn&apos;t. Here&apos;s what that actually means — concretely.
                </p>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {pillars.map((p) => (
                        <article
                            key={p.title}
                            className="bg-card border border-border rounded-xl p-8 hover:border-primary/30 hover:shadow-[0_8px_32px_rgba(10,35,24,0.06)] transition-all"
                            data-testid={`trust-pillar-${p.title.slice(0, 16).replace(/\s+/g, "-").toLowerCase()}`}
                        >
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-full bg-accent-bright/15 grid place-items-center shrink-0 mt-1">
                                    <p.icon className="w-5 h-5 text-accent-bright" strokeWidth={1.5} aria-hidden="true" />
                                </div>
                                <h2 className="font-serif text-xl text-primary leading-snug pt-1">
                                    {p.title}
                                </h2>
                            </div>
                            <p className="mt-4 text-[15px] leading-[1.75] text-foreground/80">
                                {p.body}
                            </p>
                        </article>
                    ))}
                </div>

                <div className="mt-14 bg-primary text-primary-foreground rounded-2xl p-10 md:p-12 relative overflow-hidden">
                    <div className="absolute inset-0 noise opacity-40 pointer-events-none" aria-hidden />
                    <div className="relative max-w-3xl">
                        <div className="text-[11px] uppercase tracking-[0.2em] text-accent-bright">
                            In practical terms
                        </div>
                        <h2 className="font-serif text-3xl lg:text-4xl mt-3 leading-tight text-balance">
                            What this means the next time you use a calculator here
                        </h2>
                        <ul className="mt-6 space-y-3 text-[15px] leading-relaxed text-primary-foreground/90">
                            <li className="flex gap-3"><span className="text-accent-bright font-mono text-sm shrink-0 mt-1">01 —</span> You can type your real salary, mortgage balance, or retirement nest egg. None of it is sent anywhere.</li>
                            <li className="flex gap-3"><span className="text-accent-bright font-mono text-sm shrink-0 mt-1">02 —</span> Close the tab. Your inputs vanish. We never had them in the first place.</li>
                            <li className="flex gap-3"><span className="text-accent-bright font-mono text-sm shrink-0 mt-1">03 —</span> You won&apos;t get an email from us because you calculated a mortgage. We don&apos;t have your email.</li>
                            <li className="flex gap-3"><span className="text-accent-bright font-mono text-sm shrink-0 mt-1">04 —</span> No lender will call you. We don&apos;t sell leads.</li>
                            <li className="flex gap-3"><span className="text-accent-bright font-mono text-sm shrink-0 mt-1">05 —</span> The ads you see fund everything else being free. If you use an ad blocker, we respect that — the calculators work either way.</li>
                        </ul>
                    </div>
                </div>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                    <Link to="/privacy" className="group p-5 rounded-xl border border-border hover:border-primary/40 transition-colors" data-testid="trust-link-privacy">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-accent">Read in detail</div>
                        <div className="font-serif text-xl text-primary mt-1 group-hover:underline decoration-accent-bright decoration-2 underline-offset-4">
                            Privacy Policy →
                        </div>
                    </Link>
                    <Link to="/accessibility" className="group p-5 rounded-xl border border-border hover:border-primary/40 transition-colors" data-testid="trust-link-accessibility">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-accent">Read in detail</div>
                        <div className="font-serif text-xl text-primary mt-1 group-hover:underline decoration-accent-bright decoration-2 underline-offset-4">
                            Accessibility Statement →
                        </div>
                    </Link>
                    <Link to="/terms" className="group p-5 rounded-xl border border-border hover:border-primary/40 transition-colors" data-testid="trust-link-terms">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-accent">Read in detail</div>
                        <div className="font-serif text-xl text-primary mt-1 group-hover:underline decoration-accent-bright decoration-2 underline-offset-4">
                            Terms of Use →
                        </div>
                    </Link>
                </div>
            </section>
        </>
    );
}
